/**
 * Core Constants — Centralized configuration values
 * Single source of truth for all domain-wide constants.
 */

// ==========================================
// POS / ORDERS
// ==========================================

export const REALTIME_ORDER_HOURS = 24;
export const REALTIME_ORDERS_LIMIT = 200;
export const PAGE_SIZE_ORDERS = 20;
export const MAX_ON_DEMAND_ORDERS = 500;

export const DELIVERY_SOURCES = Object.freeze(['grabfood', 'shopeefood', 'befood'] as const);

export const ORDER_STATUS = Object.freeze({
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  PREPARING: 'preparing',
  READY: 'ready',
  COMPLETED: 'completed',
  VOIDED: 'voided',
  CANCELLED: 'cancelled',
} as const);

export const INVOICE_STATUS = Object.freeze({
  NOT_ISSUED: 'not_issued',
  ISSUED: 'issued',
  CANCELLED: 'cancelled',
} as const);

// ==========================================
// INVENTORY
// ==========================================

export const MAX_BATCH_SIZE = 500;
export const MAX_TRANSACTION_RETRIES = 3;
export const RETRY_DELAY_BASE_MS = 500;
export const DEFAULT_INVENTORY_DOC_ID = 'data';

export const STOCK_STATUS = Object.freeze({
  IN_STOCK: 'in_stock',
  LOW_STOCK: 'low_stock',
  OUT_OF_STOCK: 'out_of_stock',
} as const);

export const PRICE_INCREASE_ALERT_THRESHOLD = 10;
export const PRICE_INCREASE_WARNING_THRESHOLD = 20;
export const DEFAULT_REORDER_BUFFER_DAYS = 7;

// ==========================================
// HRM / PAYROLL
// ==========================================

export const STANDARD_WORK_DAYS = 26;
export const OVERTIME_RATE = 1.5;
export const LATE_PENALTY_RATE = 2000;
export const ABSENT_PENALTY_RATE = 1.5;
export const LATE_GRACE_PERIOD_MINUTES = 15;
export const PERFECT_ATTENDANCE_BONUS = 200000;
export const FREQUENT_LATE_PENALTY_THRESHOLD = 3;
export const FREQUENT_LATE_PENALTY_AMOUNT = 100000;

export const SALARY_TYPE = Object.freeze({
  MONTHLY: 'monthly',
  HOURLY: 'hourly',
} as const);

export const PAYROLL_STATUS = Object.freeze({
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  PAID: 'paid',
} as const);

// ==========================================
// IDENTITY / ROLES
// ==========================================

export const USER_ROLE = Object.freeze({
  MASTER: 'master',
  OWNER: 'owner',
  MANAGER: 'manager',
  AREA_MANAGER: 'area_manager',
  BRANCH_MANAGER: 'branch_manager',
  STAFF: 'staff',
} as const);

export const STORAGE_KEYS = Object.freeze({
  OWNER_ID: 'fnb_system_owner_id',
  ROLE: 'fnb_role',
  BRAND_INFO: 'marketing_ai_brand_info',
} as const);

export const ADMIN_ONLY_TABS = Object.freeze([
  'financial', 'trends', 'competitor', 'content', 'promo_factory',
  'inventory', 'central_inventory', 'suppliers', 'hrm', 'brand',
  'crm', 'pos_monitor', 'settings', 'menu_generator', 'growth_hub',
  'supply_chain', 'operations',
] as const);

// ==========================================
// SECURITY
// ==========================================

export const STEP_UP_TIMEOUT_MS = 15 * 60 * 1000;
export const MAX_PIN_ATTEMPTS = 5;
export const PIN_LOCKOUT_DURATION_MS = 15 * 60 * 1000;
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;
export const LAZY_LOAD_TIMEOUT_MS = 10000;

// ==========================================
// FIRESTORE PATHS
// ==========================================

export const FIRESTORE_PATHS = {
  userDoc: (uid: string) => `users/${uid}`,
  orders: (uid: string) => `users/${uid}/orders`,
  order: (uid: string, orderId: string) => `users/${uid}/orders/${orderId}`,
  inventory: (uid: string, locationId?: string) => `users/${uid}/inventory/${locationId || 'data'}`,
  brandKit: (uid: string) => `users/${uid}/brand_kit/info`,
  voidLogs: (uid: string) => `users/${uid}/void_logs`,
  notifications: (uid: string) => `users/${uid}/notifications`,
  shifts: (uid: string) => `users/${uid}/shifts`,
  shift: (uid: string, shiftId: string) => `users/${uid}/shifts/${shiftId}`,
  shiftTransactions: (uid: string, shiftId: string) => `users/${uid}/shifts/${shiftId}/transactions`,
  staff: (uid: string) => `users/${uid}/hrm/data/staff`,
  attendance: (uid: string) => `users/${uid}/hrm/data/attendance`,
  profileMetadata: (uid: string) => `users/${uid}/profile/metadata`,
  managers: (uid: string) => `users/${uid}/managers`,
  managerIndex: (uid: string) => `manager_index/${uid}`,
  securitySettings: (uid: string) => `users/${uid}/security/settings`,
  legacyProfileMetadata: (appId: string, uid: string) => `artifacts/${appId}/users/${uid}/profile/metadata`,
  legacyStaff: (appId: string, uid: string) => `artifacts/${appId}/users/${uid}/staff`,
  legacyAttendance: (appId: string, uid: string) => `artifacts/${appId}/users/${uid}/attendance`,
  legacyShiftTransactions: (appId: string, uid: string, shiftId: string) =>
    `artifacts/${appId}/users/${uid}/shifts/${shiftId}/transactions`,
  transfers: (uid: string) => `users/${uid}/inventory/transfers`,
  transfer: (uid: string, transferId: string) => `users/${uid}/inventory/transfers/${transferId}`,
  customers: (uid: string) => `users/${uid}/customers`,
  customer: (uid: string, cid: string) => `users/${uid}/customers/${cid}`,
  legacyCustomers: (appId: string, uid: string) => `artifacts/${appId}/users/${uid}/customers`,
  legacyCustomer: (appId: string, uid: string, cid: string) => `artifacts/${appId}/users/${uid}/customers/${cid}`,
  locations: (uid: string) => `users/${uid}/locations`,
  location: (uid: string, locationId: string) => `users/${uid}/locations/${locationId}`,
  legacyPromoRules: (appId: string, uid: string) => `artifacts/${appId}/users/${uid}/promo_rules`,
  legacyPromoRule: (appId: string, uid: string, ruleId: string) => `artifacts/${appId}/users/${uid}/promo_rules/${ruleId}`,
  // System-level (Master)
  systemConfig: 'system/config',
  systemAnalytics: 'system/analytics',
  systemOwners: 'system/owners',
} as const;

// ==========================================
// AUDIT
// ==========================================

export const AUDIT_SEVERITY = Object.freeze({
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
} as const);

export const AUDIT_MODULE = Object.freeze({
  POS: 'POS',
  INVENTORY: 'Inventory',
  HRM: 'HRM',
  FINANCE: 'FINANCE',
  IDENTITY: 'IDENTITY',
  SECURITY: 'SECURITY',
  SYSTEM: 'SYSTEM',
} as const);

// ==========================================
// AI
// ==========================================

export const DEFAULT_AI_MODEL = 'gemini-2.0-flash';
export const MAX_AI_TOKENS = 8192;
